import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getUserSubscription, getUserOrders, getSubscriptionPlanName, isSubscriptionActive } from '../lib/stripe';
import { STRIPE_PRODUCTS, formatPrice } from '../stripe-config';
import { User, CreditCard, Package, Clock, CheckCircle, XCircle, Loader2, ExternalLink } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const AccountPage: React.FC = () => {
  const { userProfile } = useAuth();
  const [subscription, setSubscription] = useState<any>(null);
  const [orders, setOrders] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const [subData, ordersData] = await Promise.all([
          getUserSubscription(),
          getUserOrders()
        ]);
        
        setSubscription(subData);
        setOrders(ordersData || []);
      } catch (err) {
        console.error('Error fetching user data:', err);
        setError('Failed to load account information');
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 text-indigo-500 animate-spin mx-auto mb-4" />
          <p className="text-gray-400">Loading account information...</p>
        </div>
      </div>
    );
  }

  const planName = getSubscriptionPlanName(subscription?.price_id);
  const active = isSubscriptionActive(subscription?.subscription_status);
  const product = subscription?.price_id 
    ? STRIPE_PRODUCTS.find(p => p.priceId === subscription.price_id)
    : null;

  return (
    <div className="min-h-screen bg-gray-900 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-3xl font-bold text-white mb-8">Account Settings</h1>

        {error && (
          <div className="mb-8 bg-red-900 border border-red-700 rounded-lg p-4 flex items-center">
            <XCircle className="w-5 h-5 text-red-400 mr-2 flex-shrink-0" />
            <span className="text-red-300">{error}</span>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {/* Profile Section */}
          <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
            <div className="flex items-center space-x-3 mb-6">
              <User className="w-6 h-6 text-indigo-400" />
              <h2 className="text-xl font-semibold text-white">Profile</h2>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-gray-400 mb-1">Name</label>
                <div className="text-white font-medium">{userProfile?.full_name}</div>
              </div>
              
              <div>
                <label className="block text-sm text-gray-400 mb-1">Email</label>
                <div className="text-white font-medium">{userProfile?.email}</div>
              </div>
              
              <div>
                <label className="block text-sm text-gray-400 mb-1">Role</label>
                <div className="text-white font-medium">{userProfile?.role_name || 'User'}</div>
              </div>
              
              <div>
                <label className="block text-sm text-gray-400 mb-1">Department</label>
                <div className="text-white font-medium">{userProfile?.department || 'Not assigned'}</div>
              </div>
            </div>
          </div>

          {/* Subscription Section */}
          <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-3">
                <CreditCard className="w-6 h-6 text-green-400" />
                <h2 className="text-xl font-semibold text-white">Subscription</h2>
              </div>
              <button
                onClick={() => navigate('/pricing')}
                className="text-xs text-indigo-400 hover:text-indigo-300 flex items-center"
              >
                <span>View Plans</span>
                <ExternalLink className="w-3 h-3 ml-1" />
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-gray-400 mb-1">Current Plan</label>
                <div className="text-white font-medium">{planName}</div>
              </div>
              
              <div>
                <label className="block text-sm text-gray-400 mb-1">Status</label>
                <div className="flex items-center">
                  {active ? (
                    <>
                      <CheckCircle className="w-4 h-4 text-green-400 mr-2" />
                      <span className="text-green-400 font-medium">Active</span>
                    </>
                  ) : (
                    <>
                      <XCircle className="w-4 h-4 text-gray-400 mr-2" />
                      <span className="text-gray-400 font-medium">
                        {subscription?.subscription_status === 'canceled' ? 'Canceled' : 'Inactive'}
                      </span>
                    </>
                  )}
                </div>
              </div>
              
              {active && subscription?.current_period_end && (
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Renews On</label>
                  <div className="text-white font-medium">
                    {new Date(subscription.current_period_end * 1000).toLocaleDateString()}
                  </div>
                </div>
              )}
              
              {product && (
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Price</label>
                  <div className="text-white font-medium">
                    {formatPrice(product.price)}/month
                  </div>
                </div>
              )}
              
              {subscription?.payment_method_last4 && (
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Payment Method</label>
                  <div className="text-white font-medium">
                    {subscription.payment_method_brand} •••• {subscription.payment_method_last4}
                  </div>
                </div>
              )}
              
              <div className="pt-4">
                <a 
                  href="/pricing" 
                  className="inline-block px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm transition-colors"
                >
                  {active ? 'Change Plan' : 'Subscribe Now'}
                </a>
              </div>
            </div>
          </div>

          {/* Billing History Section */}
          <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
            <div className="flex items-center space-x-3 mb-6">
              <Clock className="w-6 h-6 text-yellow-400" />
              <h2 className="text-xl font-semibold text-white">Billing History</h2>
            </div>
            
            {orders.length > 0 ? (
              <div className="space-y-4">
                {orders.map((order) => (
                  <div key={order.order_id} className="p-3 bg-gray-700 rounded-lg">
                    <div className="flex justify-between items-center mb-2">
                      <div className="text-sm font-medium text-white">
                        Order #{order.order_id.toString().slice(-6)}
                      </div>
                      <div className="text-xs text-gray-400">
                        {new Date(order.order_date).toLocaleDateString()}
                      </div>
                    </div>
                    <div className="flex justify-between items-center">
                      <div className="text-sm text-gray-300">
                        {formatPrice(order.amount_total / 100, order.currency)}
                      </div>
                      <div className="text-xs px-2 py-1 bg-green-900 text-green-300 rounded-full">
                        {order.payment_status}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Package className="w-8 h-8 text-gray-500 mx-auto mb-2" />
                <p className="text-gray-400">No billing history yet</p>
              </div>
            )}
          </div>
        </div>

        {/* Subscription Features */}
        <div className="mt-8 bg-gray-800 rounded-xl p-6 border border-gray-700">
          <h2 className="text-xl font-semibold text-white mb-6">Your Plan Features</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {product ? (
              <>
                {product.name === 'ADAPT personal' && (
                  <>
                    <div className="flex items-start space-x-3">
                      <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <h3 className="text-white font-medium">Strategic Dashboard</h3>
                        <p className="text-sm text-gray-400">Real-time monitoring of strategic goals</p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <h3 className="text-white font-medium">Experiment Hub</h3>
                        <p className="text-sm text-gray-400">Hypothesis generation and validation</p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <h3 className="text-white font-medium">Prioritised Backlog</h3>
                        <p className="text-sm text-gray-400">Strategic delivery management</p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <h3 className="text-white font-medium">Market Insights</h3>
                        <p className="text-sm text-gray-400">Market analysis and opportunity identification</p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <h3 className="text-white font-medium">AI Co-Pilot (Limited)</h3>
                        <p className="text-sm text-gray-400">Basic AI assistance features</p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <h3 className="text-white font-medium">Single User</h3>
                        <p className="text-sm text-gray-400">Access for one user account</p>
                      </div>
                    </div>
                  </>
                )}
                
                {product.name === 'ADAPT start-up' && (
                  <>
                    <div className="flex items-start space-x-3">
                      <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <h3 className="text-white font-medium">All Personal Features</h3>
                        <p className="text-sm text-gray-400">Everything in the Personal plan</p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <h3 className="text-white font-medium">Full AI Co-Pilot Access</h3>
                        <p className="text-sm text-gray-400">Advanced AI assistance capabilities</p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <h3 className="text-white font-medium">Role-Specific Dashboards</h3>
                        <p className="text-sm text-gray-400">Tailored views for different roles</p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <h3 className="text-white font-medium">Team Collaboration</h3>
                        <p className="text-sm text-gray-400">Shared workspaces and insights</p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <h3 className="text-white font-medium">Up to 20 Users</h3>
                        <p className="text-sm text-gray-400">Multi-user access for your team</p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <h3 className="text-white font-medium">Priority Support</h3>
                        <p className="text-sm text-gray-400">Faster response times for support</p>
                      </div>
                    </div>
                  </>
                )}
                
                {product.name === 'ADAPT Enterprise' && (
                  <>
                    <div className="flex items-start space-x-3">
                      <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <h3 className="text-white font-medium">All Start-up Features</h3>
                        <p className="text-sm text-gray-400">Everything in the Start-up plan</p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <h3 className="text-white font-medium">Unlimited Users</h3>
                        <p className="text-sm text-gray-400">No user limit for large teams</p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <h3 className="text-white font-medium">Advanced Analytics</h3>
                        <p className="text-sm text-gray-400">Deep insights and custom reporting</p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <h3 className="text-white font-medium">Custom Integrations</h3>
                        <p className="text-sm text-gray-400">Connect with your enterprise systems</p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <h3 className="text-white font-medium">Dedicated Account Manager</h3>
                        <p className="text-sm text-gray-400">Personalized support and guidance</p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <h3 className="text-white font-medium">Enterprise SLAs</h3>
                        <p className="text-sm text-gray-400">Guaranteed service levels</p>
                      </div>
                    </div>
                  </>
                )}
              </>
            ) : (
              <>
                <div className="flex items-start space-x-3">
                  <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="text-white font-medium">Strategic Dashboard</h3>
                    <p className="text-sm text-gray-400">Basic access to strategic dashboard</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="text-white font-medium">Experiment Hub</h3>
                    <p className="text-sm text-gray-400">Limited experiment tracking</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="text-white font-medium">Single User</h3>
                    <p className="text-sm text-gray-400">Access for one user account</p>
                  </div>
                </div>
              </>
            )}
          </div>
          
          <div className="mt-6 text-center">
            <a 
              href="/pricing" 
              className="inline-block px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors"
            >
              {active ? 'Manage Subscription' : 'Upgrade Your Plan'}
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccountPage;